import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Toast } from "primereact/toast";
import ToastService from "../../../Admin/services/ToastService";
import RegisterUserService from "../../../Admin/services/RegisterUserService";
import {
  SaPageShell,
  SaPageHeader,
  SaCard,
  SaPrimaryButton,
  SaGhostButton,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "../shared/SuperAdminUi";

const ROLE_OPTIONS = [
  { roleId: 2, roleName: "Admin" },
  { roleId: 4, roleName: "Employee" },
];

function AddClientUser() {
  const { id } = useParams();
  const location = useLocation();
  const clientIdFromLocation = location.state?.clientId;

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    contactNumber: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    panNumber: "",
    userImage: null,
    passportNumber: "",
    aadharNumber: "",
    roleId: "",
    clientId: clientIdFromLocation ?? "",
  });

  const [formError, setFormError] = useState({});
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const toast = useRef(null);
  const toastServiceRef = useRef(null);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const user = await RegisterUserService.getUserById(id);
          setForm((prev) => ({ ...prev, ...user, password: "" }));
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, [id]);

  useEffect(() => {
    if (clientIdFromLocation != null && clientIdFromLocation !== "") {
      setForm((prev) => ({ ...prev, clientId: clientIdFromLocation }));
    }
  }, [clientIdFromLocation]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError((prev) => ({ ...prev, [name]: "" }));
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const userImage = reader.result.split(",")[1];
      setForm((prev) => ({ ...prev, userImage }));
      setFormError((prev) => ({ ...prev, userImage: "" }));
    };
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const required = [
      "firstName",
      "lastName",
      "email",
      "contactNumber",
      "streetAddress",
      "city",
      "state",
      "country",
      "postalCode",
      "roleId",
      "clientId",
    ];
    if (!id) required.push("password");

    required.forEach((key) => {
      const v = form[key];
      if (v === undefined || v === null || String(v).trim() === "") {
        errors[key] = "Required";
      }
    });

    if (form.email && !emailRegex.test(form.email)) {
      errors.email = "Enter a valid email";
    }

    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  async function saveUser() {
    if (!toastServiceRef.current && toast.current) {
      toastServiceRef.current = new ToastService(toast);
    }

    const userRoles = {
      roleId: form.roleId,
      userId: id || 0,
      isRowDeleted: false,
      createdBy: "1",
      modifiedBy: "1",
      createdOn: new Date(),
      modifiedOn: new Date(),
      isActive: true,
    };

    const requestData = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
      contactNumber: form.contactNumber,
      streetAddress: form.streetAddress,
      city: form.city,
      state: form.state,
      country: form.country,
      postalCode: form.postalCode,
      roleId: form.roleId,
      panNumber: form.panNumber,
      userImage: form.userImage,
      passportNumber: form.passportNumber,
      aadharNumber: form.aadharNumber,
      userRoles: [userRoles],
      createdBy: 0,
      modifiedBy: 0,
      isActive: true,
      clientId: form.clientId,
    };

    setSaving(true);
    try {
      if (id) {
        await RegisterUserService.updateUser(id, requestData);
        toastServiceRef.current?.showSuccess("Saved", "User updated successfully.");
      } else {
        await RegisterUserService.createUser(requestData);
        toast.current?.show({ severity: "success", summary: "Saved", detail: "User added successfully.", life: 3000 });
      }
      navigate(-1);
    } catch (error) {
      if (error.response?.status === 400 && error.response.data?.errors) {
        setFormError(error.response.data.errors);
      }
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: id ? "Could not update user." : "Could not create user.",
        life: 4000,
      });
      console.error(error);
    } finally {
      setSaving(false);
    }
  }

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    saveUser();
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "0 20px",
  };

  const err = (name) =>
    formError[name] ? (
      <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>
        {typeof formError[name] === "string" ? formError[name] : JSON.stringify(formError[name])}
      </p>
    ) : null;

  return (
    <SaPageShell>
      <Toast ref={toast} />
      <SaPageHeader
        title={id ? "Edit user" : "Add client user"}
        subtitle={
          clientIdFromLocation
            ? `New team member for client ID ${clientIdFromLocation}.`
            : "Create an Admin or Employee for an agency."
        }
        actions={
          <SaGhostButton type="button" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left" style={{ marginRight: "8px" }} />
            Back
          </SaGhostButton>
        }
      />

      <form onSubmit={onSubmit}>
        <SaCard>
          <SaSectionTitle iconClass="fas fa-user">Profile</SaSectionTitle>
          <div style={grid}>
            <SaField label="First name *">
              <input name="firstName" value={form.firstName} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.firstName ? SA.danger : SA.border }} />
              {err("firstName")}
            </SaField>
            <SaField label="Last name *">
              <input name="lastName" value={form.lastName} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.lastName ? SA.danger : SA.border }} />
              {err("lastName")}
            </SaField>
            <SaField label="Email *">
              <input type="email" name="email" value={form.email} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.email ? SA.danger : SA.border }} />
              {err("email")}
            </SaField>
            {!id && (
              <SaField label="Password *">
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={changeHandler}
                  style={{ ...saInputSx, borderColor: formError.password ? SA.danger : SA.border }}
                />
                {err("password")}
              </SaField>
            )}
            <SaField label="Contact number *">
              <input
                type="text"
                name="contactNumber"
                value={form.contactNumber}
                maxLength={13}
                onChange={(e) => {
                  if (e.target.value.length <= 13) changeHandler(e);
                }}
                style={{ ...saInputSx, borderColor: formError.contactNumber ? SA.danger : SA.border }}
              />
              {err("contactNumber")}
            </SaField>
            <SaField label="Street address *">
              <input name="streetAddress" value={form.streetAddress} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.streetAddress ? SA.danger : SA.border }} />
              {err("streetAddress")}
            </SaField>
            <SaField label="City *">
              <input name="city" value={form.city} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.city ? SA.danger : SA.border }} />
              {err("city")}
            </SaField>
            <SaField label="State *">
              <input name="state" value={form.state} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.state ? SA.danger : SA.border }} />
              {err("state")}
            </SaField>
            <SaField label="Country *">
              <input name="country" value={form.country} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.country ? SA.danger : SA.border }} />
              {err("country")}
            </SaField>
            <SaField label="Postal code *">
              <input type="text" name="postalCode" value={form.postalCode} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.postalCode ? SA.danger : SA.border }} />
              {err("postalCode")}
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-id-card">Identification</SaSectionTitle>
          <div style={grid}>
            <SaField label="PAN">
              <input name="panNumber" value={form.panNumber} onChange={changeHandler} style={saInputSx} />
            </SaField>
            <SaField label="Passport">
              <input name="passportNumber" value={form.passportNumber} onChange={changeHandler} style={saInputSx} />
            </SaField>
            <SaField label="Aadhar">
              <input name="aadharNumber" value={form.aadharNumber} onChange={changeHandler} style={saInputSx} />
            </SaField>
            <SaField label="Photo" hint="Optional PNG/JPG.">
              <input type="file" accept="image/*" onChange={onFileChange} style={{ fontSize: "13px", color: SA.muted }} />
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-user-shield">Access</SaSectionTitle>
          <div style={grid}>
            <SaField label="Role *">
              <select
                name="roleId"
                value={form.roleId}
                onChange={changeHandler}
                style={{ ...saInputSx, borderColor: formError.roleId ? SA.danger : SA.border }}
              >
                <option value="">Select role</option>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.roleId} value={r.roleId}>
                    {r.roleName}
                  </option>
                ))}
              </select>
              {err("roleId")}
            </SaField>
            <SaField label="Client ID *">
              <input
                name="clientId"
                value={form.clientId}
                onChange={changeHandler}
                readOnly={clientIdFromLocation != null && clientIdFromLocation !== ""}
                style={{
                  ...saInputSx,
                  borderColor: formError.clientId ? SA.danger : SA.border,
                  background: clientIdFromLocation != null && clientIdFromLocation !== "" ? SA.bgSoft : "#fff",
                }}
              />
              {err("clientId")}
            </SaField>
          </div>

          <div
            style={{
              marginTop: "28px",
              paddingTop: "22px",
              borderTop: `1px solid ${SA.border}`,
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <SaPrimaryButton type="submit" disabled={saving} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-check"}>
              {saving ? "Saving…" : id ? "Save changes" : "Create user"}
            </SaPrimaryButton>
            <SaGhostButton type="button" onClick={() => navigate(-1)}>
              Cancel
            </SaGhostButton>
          </div>
        </SaCard>
      </form>
    </SaPageShell>
  );
}

export default AddClientUser;
